import { ArrowRight, Check, ChevronRight, Loader2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

type PartyState = 'waiting' | 'action' | 'done'

const PartyCard = ({
  party,
  state,
  statusLabel,
  description,
  actionLabel,
  onAction,
  isLoading,
}: {
  party: string
  state: PartyState
  statusLabel: string
  description: string
  actionLabel: string
  onAction?: () => void
  isLoading?: boolean
}) => {
  const isAction = state === 'action' && Boolean(onAction)
  const canClick = isAction && !isLoading

  const className = cn(
    'flex min-w-0 flex-1 items-center gap-3 rounded-[18px] px-4 py-2.5 text-left',
    'border transition-[background-color,border-color,opacity,transform,box-shadow]',
    state === 'done' && 'border-emerald-200 bg-emerald-50/70',
    state === 'waiting' && 'border-[var(--raised-border)] bg-muted/40',
    state === 'action' &&
      'border-orange-200 bg-gradient-to-br from-orange-50/90 via-white to-orange-50/40 shadow-soft',
    canClick &&
      'heartbeat-border cursor-pointer hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-new-hover active:translate-y-px',
    canClick &&
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/40 focus-visible:ring-offset-2'
  )

  const content = (
    <>
      <Column className="min-w-0 flex-1 gap-0.5">
        <Row className="items-center gap-2">
          {state === 'action' ? (
            <>
              <span className="text-[15px] font-semibold leading-snug tracking-[-0.01em] text-[#1A1C1E]">
                {actionLabel}
              </span>
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-800">
                {statusLabel}
              </span>
            </>
          ) : (
            <>
              <span className="text-[15px] font-semibold leading-snug tracking-[-0.01em] text-[#1A1C1E]">
                {party}
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-medium',
                  state === 'done' && 'bg-emerald-100 text-emerald-800',
                  state === 'waiting' && 'bg-gray-200 text-gray-600'
                )}
              >
                {statusLabel}
              </span>
              {state === 'done' ? (
                <Check
                  className="h-3.5 w-3.5 text-emerald-600"
                  strokeWidth={2.5}
                />
              ) : null}
            </>
          )}
        </Row>
        <span className="text-[13px] leading-snug text-[#6B7280]">
          {description}
        </span>
      </Column>
      {isAction ? (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E5E7EB] bg-black/80 text-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          )}
        </div>
      ) : null}
    </>
  )

  if (isAction) {
    return (
      <button
        type="button"
        onClick={canClick ? onAction : undefined}
        disabled={isLoading}
        aria-label={`${party}. ${statusLabel}. ${actionLabel}. ${description}`}
        className={cn(className, 'group')}
      >
        {content}
      </button>
    )
  }

  return (
    <div
      role="status"
      aria-label={`${party}. ${statusLabel}. ${description}`}
      className={className}
    >
      {content}
    </div>
  )
}

export const QuoteApprovalParties = ({
  isAdmin,
  klayderApproved,
  customerApproved,
  canKlayderPress,
  canCustomerPress,
  awaitingKlayder,
  isAnonymous,
  onKlayderApprove,
  onCustomerApprove,
  isKlayderLoading,
  isCustomerLoading,
}: {
  isAdmin: boolean
  klayderApproved: boolean
  customerApproved: boolean
  canKlayderPress: boolean
  canCustomerPress: boolean
  awaitingKlayder: boolean
  isAnonymous: boolean
  onKlayderApprove: () => void
  onCustomerApprove: () => void
  isKlayderLoading?: boolean
  isCustomerLoading?: boolean
}) => {
  const { t } = useLocalization()

  const klayderState: PartyState = klayderApproved
    ? 'done'
    : canKlayderPress
      ? 'action'
      : 'waiting'
  const customerState: PartyState = customerApproved
    ? 'done'
    : canCustomerPress
      ? 'action'
      : 'waiting'

  const klayderDescription = klayderApproved
    ? isAdmin
      ? t(
          'You reviewed pricing, sizes, and colors.',
          'Du granskade pris, storlekar och färger.'
        )
      : t(
          'Klayder reviewed pricing, sizes, and colors.',
          'Klayder granskade pris, storlekar och färger.'
        )
    : awaitingKlayder && isAdmin
      ? t('The customer asked you to review.', 'Kunden bad dig granska.')
      : isAdmin
        ? t(
            'Check pricing, sizes, and colors. Then the customer can approve.',
            'Kolla pris, storlekar och färger. Sedan kan kunden godkänna.'
          )
        : t(
            'Klayder reviews pricing, sizes, and colors first.',
            'Klayder granskar pris, storlekar och färger först.'
          )

  const customerDescription = customerApproved
    ? isAdmin
      ? t(
          'The customer approved. Factory proofs were requested.',
          'Kunden godkände. Korrekturer är begärda.'
        )
      : t(
          'You approved. Factory proofs were requested.',
          'Du godkände. Korrekturer är begärda.'
        )
    : klayderApproved
      ? isAnonymous
        ? t(
            'Sign up to approve and request proofs.',
            'Skapa konto för att godkänna och begära korrekturer.'
          )
        : isAdmin
          ? t(
              'The customer approves next, then we request proofs.',
              'Kunden godkänner härnäst, sedan begär vi korrekturer.'
            )
          : t(
              'Review pricing, sizes, and colors. Then we request factory proofs.',
              'Granska pris, storlekar och färger. Sedan begär vi korrekturer.'
            )
      : isAdmin
        ? t(
            'The customer approves after you have reviewed.',
            'Kunden godkänner när du har granskat.'
          )
        : t(
            'You approve after Klayder has reviewed.',
            'Du godkänner när Klayder har granskat.'
          )

  return (
    <Row className="items-stretch gap-2">
      <PartyCard
        party={isAdmin ? t('You', 'Du') : t('Klayder', 'Klayder')}
        state={klayderState}
        statusLabel={
          klayderState === 'done'
            ? t('Reviewed', 'Granskad')
            : klayderState === 'action'
              ? t('Your turn', 'Din tur')
              : t('Waiting', 'Väntar')
        }
        description={klayderDescription}
        actionLabel={t('Review quote', 'Granska offert')}
        onAction={onKlayderApprove}
        isLoading={isKlayderLoading}
      />
      <ChevronRight
        className="h-4 w-4 shrink-0 self-center text-[#9CA3AF]"
        strokeWidth={2}
      />
      <PartyCard
        party={isAdmin ? t('Customer', 'Kund') : t('You', 'Du')}
        state={customerState}
        statusLabel={
          customerState === 'done'
            ? t('Approved', 'Godkänd')
            : customerState === 'action'
              ? t('Your turn', 'Din tur')
              : t('Waiting', 'Väntar')
        }
        description={customerDescription}
        actionLabel={t('Approve quote', 'Godkänn offert')}
        onAction={onCustomerApprove}
        isLoading={isCustomerLoading}
      />
    </Row>
  )
}
